import * as React from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import { showNotification } from "jazasoft";
import { TextInput } from "jazasoft/lib/mui/components/Table";

import { useDeepEffect, useDeepMemo, useMutation, useQuery, useSafeState, useSetting, useUnit } from "../../../hooks";
import { getDistinctValues, getStyleColor, isEmpty, listFromCsv, toFixed, toFixedValue } from "../../../utils/helpers";
import { cbSortFlow, getEffPartList, getEffectiveExtraPercent, getEffectiveFpoExtraPercent } from "../../../helpers/orderHelper";

import { LabeledCheckBox } from "./ShadeRowRenderer";
import SettingKeys from "../../../utils/SettingKeys";
import { getCutOrderRows, getReadyToPlanRows } from "../cutPlanHelper";
import { DEFAULT_SEPARATOR, LotType, UOM } from "../../../constants";
import { convert } from "../../../helpers/unitConversionHelper";

const csvField = (flowInfoList, field) => getDistinctValues(flowInfoList.map(fi => fi[field]).filter(e => !isEmpty(e))).join(", ");

const UomCodeFromValue = Object.values(UOM).reduce((acc, uom) => ({ ...acc, [uom.value]: uom.code }), {});

const useCutOrder = props => {
  const id = props.match?.params?.id;

  const [version, setVersion] = useSafeState(1);
  const [tabIdx, setTabIdx] = useSafeState(0);
  const [selectedKeys, setSelectedKeys] = useSafeState([]); // For Tab: Ready To Plan
  const [shadeColumns, setShadeColumns] = useSafeState([]);
  const [shadeRows, setShadeRows] = useSafeState([]);
  const [errorMessage, setErrorMessage] = useSafeState(null);

  const history = useHistory();
  const dispatch = useDispatch();
  const { allUnitList } = useUnit();

  const [mutate, { loading: busy }] = useMutation();

  const productParts = useSetting(SettingKeys.PRODUCT_PARTS);
  const conversionFactor = useSetting(SettingKeys.UOM_CONVERSION_FACTOR);
  const conversionFactorList = JSON.parse(conversionFactor || "[]");

  const { data: order = {}, loading } = useQuery({ url: `orders/${id}`, params: { page: "cutPlan" }, version });
  const { data: ledgerData = [] } = useQuery(id && { url: `ledgers`, params: { search: `order.id==${id}`, size: 10000 }, version });

  const fabricItemIds = getDistinctValues(ledgerData.map(l => l.fabricItem?.id).filter(id => !isEmpty(id)));

  const { data: rollInfoList = [] } = useQuery(
    !isEmpty(fabricItemIds) && {
      url: `rolls`,
      params: { search: `fabricItem.id=in=(${fabricItemIds.join(",")});parentId=='NULL'`, size: 100000 },
      version
    }
  );

  const flowInfoRows = useDeepMemo(() => {
    if (isEmpty(order)) return [];

    let rowList = [];
    if (tabIdx === 0) {
      rowList = getCutOrderRows(order, productParts, LotType.BULK);
      rowList = rowList
        .map(row => {
          const cutOrder = row.cutOrder;
          const styleColorList = cutOrder?.styleColorList || [];
          const ledgerList = ledgerData
            .filter(
              l =>
                cutOrder.productIdList?.includes(l.productId) &&
                styleColorList.includes(getStyleColor(l.style, l.styleColor)) &&
                l.part === cutOrder.part
            )
            .filter(l => isEmpty(cutOrder.parentColor) || cutOrder.parentColor === l.parentStyleColor);
          const ledger = !isEmpty(ledgerList) ? ledgerList.find(e => e.style === row.style && e.styleColor === row.color) : {};

          const allocatedRollList = rollInfoList.filter(ri => ri.cutOrderFlowId === cutOrder?.id);

          const orderQty = row?.orderQty || 0;
          const uom = row?.uom;
          const ocu = row?.ocu || 0;

          const allocatedLength = allocatedRollList.reduce((acc, ri) => acc + ri.length, 0);
          const allocatedWeight = allocatedRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const allocatedFabric =
            uom === UOM.KILOGRAM.value
              ? allocatedWeight
              : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

          const expOrderQty = ocu > 0 && allocatedFabric > 0 ? Number(toFixed(allocatedFabric / ocu, 0)) : null;
          const expCutPercent = orderQty > 0 && expOrderQty > 0 ? Number(toFixed((expOrderQty * 100) / orderQty, 1)) : null;

          const allocated = isEmpty(row.parentKey) ? toFixed(allocatedFabric) : null;

          return {
            ...row,
            itemCode: ledger?.fabric?.itemCode,
            fabColor: ledger?.fabColor,
            fabricId: ledger?.fabric?.id,
            allocatedRollList,
            allocated,
            expOrderQty: !isEmpty(allocated) ? expOrderQty : null,
            expCutPercent: !isEmpty(allocated) ? expCutPercent : null,
            status: !isEmpty(allocated) ? cutOrder.state : null
          };
        })
        .map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
    } else {
      rowList = getReadyToPlanRows(order);

      const partSerial = !isEmpty(productParts) ? listFromCsv(productParts).reduce((acc, part, idx) => ({ ...acc, [part]: idx }), {}) : {};
      rowList = rowList
        .sort((a, b) => cbSortFlow(a.groupOrder, b.groupOrder, partSerial))
        .map(row => {
          const groupOrder = row.groupOrder;
          const styleColorList = groupOrder?.styleColorList || [];
          const ledgerList = ledgerData
            .filter(
              l =>
                groupOrder.productIdList?.includes(l.productId) &&
                styleColorList.includes(getStyleColor(l.style, l.styleColor)) &&
                l.part === groupOrder.part
            )
            .filter(l => isEmpty(groupOrder.parentColor) || groupOrder.parentColor === l.parentStyleColor);
          const ledger = !isEmpty(ledgerList) ? ledgerList.find(e => e.style === row.style && e.styleColor === row.color) : {};

          const allocatedRollList = rollInfoList.filter(ri => ri.orderFlowId === groupOrder?.id);
          const plannedRollList = rollInfoList.filter(ri => ri.orderFlowId === groupOrder?.id && !isEmpty(ri.cutOrderFlowId));
          const orderQty = row?.orderQty || 0;
          const uom = row?.uom;
          const ocu = row?.ocu || 0;

          const allocatedLength = allocatedRollList.reduce((acc, ri) => acc + ri.length, 0);
          const allocatedWeight = allocatedRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const allocated =
            uom === UOM.KILOGRAM.value
              ? allocatedWeight
              : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });
          const plannedLength = plannedRollList.reduce((acc, ri) => acc + ri.length, 0);
          const plannedWeight = plannedRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);
          const planned =
            uom === UOM.KILOGRAM.value ? plannedWeight : convert({ input: plannedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

          const cutOrderAllocatedLength = row.cutOrderList?.reduce((acc, c) => acc + (c.allocatedLength || 0), 0);
          const cutOrderAllocatedWeight = row.cutOrderList?.reduce((acc, c) => acc + (c.allocatedWeight || 0), 0);
          const cutOrderAllocated =
            uom === UOM.KILOGRAM.value
              ? cutOrderAllocatedWeight
              : convert({ input: cutOrderAllocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

          const finalAllocated = isEmpty(row.parentKey) ? Number(toFixed(allocated, 1)) : Number(toFixed(cutOrderAllocated, 1));

          const expOrderQty = ocu > 0 && finalAllocated > 0 ? Number(toFixed(finalAllocated / ocu, 0)) : null;
          const expCutPercent = orderQty > 0 && expOrderQty > 0 ? Number(toFixed((expOrderQty * 100) / orderQty, 1)) : null;

          return {
            ...row,
            itemCode: ledger?.fabric?.itemCode,
            fabColor: ledger?.fabColor,
            fabricId: ledger?.fabric?.id,
            allocatedRollList,
            allocated: finalAllocated,
            planned: isEmpty(row.parentKey) ? Number(toFixed(planned, 1)) : null,
            expOrderQty: expOrderQty,
            expCutPercent: expCutPercent,
            status: finalAllocated > 0 ? groupOrder.state : "-"
          };
        })
        .map(row => ({ ...row, poRef: order?.poRef, customerName: order?.customer?.name, seasonName: order?.season?.name }));
    }

    return rowList;
  }, [order, tabIdx, ledgerData, rollInfoList, productParts, conversionFactorList]);

  const flowInfoColumns = useDeepMemo(() => {
    let cols = [
      { dataKey: "poRef", title: "PO Ref" },
      { dataKey: "customerName", title: "Customer" },
      { dataKey: "seasonName", title: "Season" },
      { dataKey: "unitId", title: "Unit", render: value => allUnitList?.find(e => e.unitId === value)?.name || value, highlight: true },
      { dataKey: "fpo", title: "FPO", highlight: true },
      { dataKey: "externalFpo", title: "Ext. FPO", highlight: true },
      { dataKey: "flowRef", title: "Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "customerFlowRef", title: "Customer Flow Ref.", render: (value, shortValue) => shortValue || "-", highlight: true },
      { dataKey: "productName", title: "Product", filterOptions: { filter: true }, highlight: true },
      { dataKey: "style", title: "Style", filterOptions: { filter: true }, highlight: true },
      { dataKey: "color", title: "Style Color", filterOptions: { filter: true }, highlight: true },
      { dataKey: "effPartName", title: "Part", highlight: true },
      { dataKey: "lotValue", title: "Lot", highlight: true },
      { dataKey: "inseam", title: "Inseam", filterOptions: { filter: true }, highlight: true },
      { dataKey: "destination", title: "Destination", filterOptions: { filter: true }, highlight: true },
      { dataKey: "deliveryDate", title: "Delivery Date", filterOptions: { filter: true }, highlight: true },
      { dataKey: "orderQty", title: "Order Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "fpoQty", title: "FPO Qty", render: (value, _, row) => `${value} ${row.uomQty || "Pc"}` },
      { dataKey: "extra", title: "Extra \nShip (%)", render: value => (isEmpty(value) ? "0%" : `${value}%`) },
      {
        dataKey: "ocu",
        title: "BOM CU",
        render: (value, _, row) => (!isEmpty(value) && value > 0 ? `${toFixedValue(value, 3)} ${UomCodeFromValue[row.uom]}` : "")
      },
      {
        dataKey: "itemCode",
        title: "Item Code",
        linkPath: row => history.push(`/warehouse/fabrics/${row?.fabricId}/view`)
      },
      { dataKey: "fabColor", title: "Fabric Color" },
      {
        dataKey: "allocated",
        title: "Allocated \nFabric",
        align: "right",
        render: (value, _, row) => (!isEmpty(value) && value > 0 ? `${value} ${UomCodeFromValue[row.uom]}` : "-")
      },
      {
        dataKey: "alreadyPlanned",
        title: "Already \nPlanned",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`)
      },
      { dataKey: "expOrderQty", title: "Exp. Cut Qty", render: (value, _, row) => (isEmpty(value) ? "" : `${value} (${row.expCutPercent}%)`) },
      { dataKey: "status", title: "Status" }
    ];

    if (flowInfoRows.every(e => isEmpty(e.flowRef))) {
      cols = cols.filter(c => c.dataKey !== "flowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.customerFlowRef))) {
      cols = cols.filter(c => c.dataKey !== "customerFlowRef");
    }
    if (flowInfoRows.every(e => isEmpty(e.inseam))) {
      cols = cols.filter(c => c.dataKey !== "inseam");
    }
    if (flowInfoRows.every(e => isEmpty(e.destination))) {
      cols = cols.filter(c => c.dataKey !== "destination");
    }
    if (flowInfoRows.every(e => isEmpty(e.deliveryDate))) {
      cols = cols.filter(c => c.dataKey !== "deliveryDate");
    }
    if (flowInfoRows.every(e => isEmpty(e.fpo))) {
      cols = cols.filter(c => c.dataKey !== "fpo");
    }
    if (flowInfoRows.every(e => isEmpty(e.externalFpo))) {
      cols = cols.filter(c => c.dataKey !== "externalFpo");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => !["unitId", "fpo", "externalFpo", "fpoQty"].includes(c.dataKey));
    }

    return cols;
  }, [tabIdx, flowInfoRows, allUnitList]);

  //** Redirect to Planning Tab if Cut Order not created yet **/
  useDeepEffect(() => {
    if (isEmpty(order) || loading) return;
    if (tabIdx === 0 && isEmpty(flowInfoRows)) {
      setTabIdx(1);
    }
  }, [order, loading]);

  const selectedRows = tabIdx === 0 ? [] : flowInfoRows?.filter(fi => selectedKeys?.includes(fi.key)) || [];

  //* FabricInfo Table Rows and Columns *//
  const fabricInfoRows = useDeepMemo(() => {
    if (tabIdx === 0 || !isEmpty(errorMessage) || isEmpty(selectedRows)) return [];

    let rowList = [];
    const groupOrder = selectedRows[0]?.groupOrder || {};
    if (groupOrder.styleColorList?.length > 1) {
      rowList = groupOrder.styleColorList.map(styleColor => {
        const [style, color] = styleColor.split(DEFAULT_SEPARATOR);

        const ledgerList = ledgerData
          .filter(l => groupOrder.productIdList?.includes(l.productId) && l.style === style && l.styleColor === color && l.part === groupOrder.part)
          .filter(l => isEmpty(groupOrder.parentColor) || groupOrder.parentColor === l.parentStyleColor);
        const ledger = !isEmpty(ledgerList) ? ledgerList[0] : {};

        const parentKeys = getDistinctValues(selectedRows.map(e => e.parentKey).filter(key => !isEmpty(key)));
        const parentKey = parentKeys[0];
        const parent = isEmpty(parentKey) ? selectedRows[0] : flowInfoRows.find(e => e.key === parentKey);
        const selectedChildren = isEmpty(parentKey) ? selectedRows : selectedRows.filter(fi => fi.parentKey === parentKey);
        const flowInfoList = selectedChildren.flatMap(e => e.flowInfoList || []).filter(fi => fi.style === style && fi.color === color);

        const allocatedRollList = parent?.allocatedRollList?.filter(ri => ri.style === style && ri.color === color);

        const orderQty = flowInfoList.reduce((acc, e) => acc + e.orderQty, 0);
        const fpoQty = flowInfoList.reduce((acc, e) => acc + (e.fpoQty || 0), 0);
        const partList = getEffPartList(flowInfoList, order);
        const part = partList.find(p => p.effPartName === groupOrder.part) || {};
        const uom = part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom;
        const ocu = part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: part.uom, uomOut: UOM.KILOGRAM.value }) : part.ocu;

        const allocatedLength = allocatedRollList?.reduce((acc, ri) => acc + ri.length, 0);
        const allocatedWeight = allocatedRollList?.reduce((acc, ri) => acc + (ri.weight || 0), 0);
        const allocated =
          uom === UOM.KILOGRAM.value
            ? allocatedWeight
            : convert({ input: allocatedLength, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList });

        const expOrderQty = ocu > 0 && allocated > 0 ? toFixed(allocated / ocu, 0) : null;
        const expCutPercent = orderQty > 0 && expOrderQty > 0 ? toFixed((expOrderQty * 100) / orderQty, 1) : null;

        return {
          style,
          styleColor: color,
          orderQty,
          fpoQty,
          ocu: toFixedValue(ocu, 3),
          uom,
          itemCode: ledger?.fabric?.itemCode,
          fabColor: ledger?.fabColor,
          allocated: toFixed(allocated, 2),
          expOrderQty: expOrderQty,
          expCutPercent: expCutPercent
        };
      });
    }

    return rowList;
  }, [flowInfoRows, selectedKeys, rollInfoList]);

  const fabricInfoColumns = useDeepMemo(() => {
    if (tabIdx === 0) return [];

    let cols = [
      { dataKey: "style", title: "Style" },
      { dataKey: "styleColor", title: "FG Color" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "fpoQty", title: "FPO Qty" },
      { dataKey: "ocu", title: "BOM CU", render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`) },
      {
        dataKey: "itemCode",
        title: "Item Code",
        linkPath: row =>
          history.push(`/warehouse/quality/${id}/po-view`, {
            flowInfoId: row.id,
            partId: row.part?.id,
            parentId: row.parentId
          })
      },
      { dataKey: "fabColor", title: "Fabric Color" },
      {
        dataKey: "allocated",
        title: "Allocated Fabric",
        render: (value, _, row) => (isEmpty(value) ? "-" : `${value} ${UomCodeFromValue[row.uom]}`)
      },
      // { dataKey: "alreadyPlanned", title: "Already Cut/Planned" }, //TODO: align
      { dataKey: "expOrderQty", title: "Exp. Cut Qty", render: (value, _, row) => (isEmpty(value) ? "" : `${value} (${row.expCutPercent}%)`) }
    ];
    const styleCount = getDistinctValues(fabricInfoRows.map(e => e.style))?.length;
    const colorCount = getDistinctValues(fabricInfoRows.map(e => e.color))?.length;
    if (styleCount <= 1) {
      cols = cols.filter(c => c.dataKey !== "style");
    }
    if (colorCount <= 1) {
      cols = cols.filter(c => c.dataKey !== "color");
    }
    if (allUnitList?.length <= 1) {
      cols = cols.filter(c => c.dataKey !== "fpoQty");
    } else {
      cols = cols.filter(c => c.dataKey !== "orderQty");
    }

    return cols;
  }, [tabIdx, fabricInfoRows, allUnitList]);
  //* FabricInfo Table Rows and Columns *//

  //** Handle Shade Selection on Ready to Plan Tab **/
  const onChange =
    ({ rowIdx, column }) =>
    e => {
      if (column.dataKey.startsWith("shade-")) {
        let checked = e.target.checked;
        setShadeRows(rows => {
          const cellValue = rows[rowIdx][column.dataKey];
          let newRows = [...rows];
          const row = { ...newRows[rowIdx], [column.dataKey]: { checked, value: cellValue?.value } };

          //** Update Row Total **/
          const total = Object.keys(row)
            .filter(key => key.startsWith("shade-"))
            .filter(key => row[key].checked)
            .reduce((acc, key) => acc + (row[key]?.value || 0), 0);
          newRows.splice(rowIdx, 1, { ...row, total: Number(toFixed(total)) });

          //** Update Column Total **//
          if (newRows?.length > 1 && newRows[newRows.length - 1]?.isTotal) {
            const totalRow = newRows[newRows?.length - 1];
            const flowRows = newRows.slice(0, newRows.length - 1);
            const shadeQtyMap = Object.keys(totalRow)
              .filter(key => key.startsWith("shade-"))
              .reduce(
                (acc, key) => ({
                  ...acc,
                  [key]: flowRows.some(fRow => fRow[key].checked) ? flowRows[0][key]?.value : 0
                }),
                {}
              );
            const total = Number(toFixed(Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0)));
            newRows.splice(newRows.length - 1, 1, { ...totalRow, ...shadeQtyMap, total });
          }

          return newRows;
        });
      } else {
        let value = e.target.value;
        setShadeRows(rows => {
          let newRows = [...rows];
          const row = { ...newRows[rowIdx], [column.dataKey]: value };
          newRows.splice(rowIdx, 1, row);

          if (newRows?.length > 1 && newRows[newRows.length - 1]?.isTotal) {
            const totalRow = newRows[newRows?.length - 1];
            const total = newRows.slice(0, newRows.length - 1).reduce((acc, row) => acc + (Number(row[column.dataKey]) || 0), 0);
            newRows.splice(newRows.length - 1, 1, { ...totalRow, [column.dataKey]: total });
          }

          return [...newRows];
        });
      }
    };

  //** Initialize Shade Columns and Rows */
  useDeepEffect(() => {
    if (isEmpty(flowInfoRows) || isEmpty(selectedKeys)) {
      setErrorMessage(null);
      return;
    }
    let selectedRows = flowInfoRows.filter(fi => selectedKeys.includes(fi.key));
    let columns = [];
    let rows = [];

    const parentKeys = getDistinctValues(selectedRows.map(e => e.parentKey).filter(key => !isEmpty(key)));

    if (parentKeys?.length > 1 || (parentKeys?.length === 0 && selectedRows?.length > 1)) {
      setErrorMessage("FlowInfo across multiple Group Order cannot be combined");
    } else if (selectedRows?.length > 0) {
      setErrorMessage(null);

      const parentKey = parentKeys[0];
      const children = flowInfoRows.filter(fi => fi.parentKey === parentKey);
      const selectedChildren = selectedRows.filter(fi => fi.parentKey === parentKey);
      if (selectedChildren?.length < children?.length) {
        const parent = flowInfoRows.find(e => parentKey === e.key);

        const allocatedRollList = parent?.allocatedRollList?.filter(ri => isEmpty(ri.cutOrderFlowId)) || [];
        const shadeList = getDistinctValues(
          allocatedRollList
            .map(e => e.shade)
            .filter(shade => !isEmpty(shade))
            .sort((a, b) => (a < b ? -1 : 1))
        );

        if (isEmpty(shadeList)) return;

        const shadeQtyMap = allocatedRollList
          .filter(e => !isEmpty(e.shade))
          .reduce((acc, roll) => ({ ...acc, [roll.shade]: (acc[roll.shade] || 0) + roll.length }), {});

        columns = [
          { dataKey: "style", title: "Style" },
          { dataKey: "styleColor", title: "Color" },
          { dataKey: "flowRef", title: "Flow Ref" },
          { dataKey: "customerFlowRef", title: "Customer \nFlow Ref" },
          { dataKey: "inseam", title: "Inseam" },
          { dataKey: "destination", title: "Destination" },
          { dataKey: "deliveryDate", title: "Delivery Date" },
          { dataKey: "ocu", title: "BOM CU" },
          { dataKey: "orderQty", title: "Order Qty" },
          { dataKey: "fpoQty", title: "FPO Qty" },
          { dataKey: "fpoExtra", title: "Extra Cut (%)" },
          { dataKey: "fabricRequired", title: "Fab. \nRequired" },
          { dataKey: "extraFabricRequired", title: "Extra Fab. \nRequired" },
          { dataKey: "alreadyAllocated", title: "Already \nAllocated" },
          ...shadeList.map(shade => ({
            dataKey: `shade-${shade}`,
            title: `${shade}`,
            element: <LabeledCheckBox onChange={onChange} />
          })),
          { dataKey: "total", title: `Total` },
          { dataKey: "allocationQty", title: "Allocation \nQty", element: <TextInput type="number" onChange={onChange} /> }
        ];

        const groupOrder = parent?.groupOrder || {};

        rows = groupOrder.styleColorList?.map(styleColor => {
          const [style, color] = styleColor.split(DEFAULT_SEPARATOR);

          const flowInfoList = selectedChildren.flatMap(fi => fi.flowInfoList || []).filter(fi => fi.style === style && fi.color === color);

          const partList = getEffPartList(flowInfoList, order);
          const part = partList.find(p => p.effPartName === groupOrder.part) || {};
          const uom = part.uom === UOM.GRAM.value ? UOM.KILOGRAM.value : part.uom;
          const ocu = part.uom === UOM.GRAM.value ? convert({ input: part.ocu, uomIn: part.uom, uomOut: UOM.KILOGRAM.value }) : part.ocu;
          const uomCode = UomCodeFromValue[uom];

          const extra = getEffectiveExtraPercent(flowInfoList);
          const fpoExtra = getEffectiveFpoExtraPercent(flowInfoList);

          const orderQty = flowInfoList.reduce((acc, e) => acc + e.orderQty, 0);
          const fpoQty = flowInfoList.reduce((acc, e) => acc + (e.fpoQty || 0), 0);
          const finalFpoQty = allUnitList?.length <= 1 ? orderQty : fpoQty;
          const extraFpoQty = toFixed(finalFpoQty * (1 + 0.01 * fpoExtra), 0);

          const fabricRequired = Number(toFixed(finalFpoQty * ocu, 1));
          const extraFabricRequired = Number(toFixed(extraFpoQty * ocu, 1));
          const allocatedRollList =
            parent?.allocatedRollList?.filter(ri => isEmpty(ri.cutOrderFlowId))?.filter(ri => ri.style === style && ri.color === color) || [];
          const shadeList = getDistinctValues(
            allocatedRollList
              .map(e => e.shade)
              .filter(shade => !isEmpty(shade))
              .sort((a, b) => (a < b ? -1 : 1))
          );

          const shadeQtyMap = allocatedRollList
            .filter(e => !isEmpty(e.shade))
            .reduce(
              (acc, roll) => ({
                ...acc,
                [roll.shade]:
                  (acc[roll.shade] || 0) +
                  (uom === UOM.KILOGRAM.value
                    ? roll.weight || 0
                    : convert({ input: roll.length, uomIn: UOM.METER.value, uomOut: uom, conversionFactorList }))
              }),
              {}
            );

          return {
            style,
            styleColor: color,
            flowRef: csvField(flowInfoList, "flowRef"),
            customerFlowRef: csvField(flowInfoList, "customerFlowRef"),
            inseam: csvField(flowInfoList, "inseam"),
            destination: csvField(flowInfoList, "destination"),
            deliveryDate: csvField(flowInfoList, "deliveryDate"),
            orderQty,
            fpoQty,
            extraFpoQty,
            extra,
            fpoExtra,
            uomCode,
            ocu: `${toFixedValue(ocu, 3)} ${uomCode}`,
            fabricRequired: `${fabricRequired} ${uomCode}`,
            extFabricRequired: extraFabricRequired,
            extraFabricRequired: `${extraFabricRequired} ${uomCode} (${100 + extra}%)`,
            total: `${toFixed(Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0))} ${UomCodeFromValue[uom]}`,
            ...shadeList.reduce(
              (acc, shade) => ({
                ...acc,
                [`shade-${shade}`]: { checked: true, value: Number(toFixed(shadeQtyMap[shade])) }
              }),
              {}
            )
          };
        });

        if (rows.every(r => isEmpty(r.flowRef))) {
          columns = columns.filter(c => c.dataKey !== "flowRef");
        }
        if (rows.every(r => isEmpty(r.customerFlowRef))) {
          columns = columns.filter(c => c.dataKey !== "customerFlowRef");
        }
        if (rows.every(r => isEmpty(r.inseam))) {
          columns = columns.filter(c => c.dataKey !== "inseam");
        }
        if (rows.every(r => isEmpty(r.destination))) {
          columns = columns.filter(c => c.dataKey !== "destination");
        }
        if (rows.every(r => isEmpty(r.deliveryDate))) {
          columns = columns.filter(c => c.dataKey !== "deliveryDate");
        }
        if (allUnitList?.length <= 1) {
          columns = columns.filter(c => c.dataKey !== "fpoQty");
        } else {
          columns = columns.filter(c => c.dataKey !== "orderQty");
        }
        const uom = selectedRows[0]?.uom;
        if (rows?.length > 1) {
          let totalRow = {
            isTotal: true,
            [columns[0].dataKey]: "Total",
            total: `${toFixed(Object.values(shadeQtyMap).reduce((acc, qty) => acc + qty, 0))} ${UomCodeFromValue[uom]}`,
            ...shadeList.reduce((acc, shade) => ({ ...acc, [`shade-${shade}`]: `${toFixed(shadeQtyMap[shade])} ${UomCodeFromValue[uom]}` }), {})
          };
          rows = [...rows, totalRow];
        }
      }
    }

    setShadeColumns(columns);
    setShadeRows(rows);
  }, [flowInfoRows, selectedKeys, allUnitList, conversionFactorList]);

  const createCutOrder = () => {
    const parentKeys = getDistinctValues(selectedRows.map(e => e.parentKey).filter(key => !isEmpty(key)));
    const flowInfoList = selectedRows.filter(row => isEmpty(parentKeys) || !parentKeys.includes(row.key)).flatMap(e => e.flowInfoList || []);

    let shadeErrors = [];
    let allocWarnings = [];
    const allowedShadeList = shadeRows
      .filter(e => !e.isTotal)
      .map(shadeRow => {
        const allowedShades = Object.keys(shadeRow)
          .filter(key => key.startsWith("shade-"))
          .filter(key => shadeRow[key].checked)
          .map(key => key.substring(6))
          .join(",");
        const shades = Object.keys(shadeRow)
          .filter(key => key.startsWith("shade-"))
          .filter(key => shadeRow[key].checked);
        const shadeQty = shades.map(shade => shadeRow[shade]?.value || 0).reduce((acc, qty) => acc + qty, 0);
        const allocationQty = shadeRow?.allocationQty || 0;
        if (allocationQty <= 0) {
          shadeErrors.push(`${shadeRow.style} -> ${shadeRow.styleColor}`);
        } else if (shadeQty < allocationQty) {
          shadeErrors.push(`${shadeRow.style} -> ${shadeRow.styleColor}: Current allocation = ${allocationQty}, Shade Qty = ${shadeQty}`);
        }
        if (allocationQty > shadeRow.extFabricRequired + 1) {
          allocWarnings.push(
            `Fabric Over allocation. Fabric required for ${shadeRow.extraFpoQty} pcs as per BOM consumption is - ${shadeRow.extFabricRequired} m (${shadeRow.style} -> ${shadeRow.styleColor})`
          );
        }
        return { style: shadeRow.style, color: shadeRow.styleColor, allowedShades, shadeQty, allocationQty };
      })
      .filter(e => e.shadeQty > 0);
    if (allowedShadeList.some(e => e.allocationQty <= 0)) {
      dispatch(showNotification(`Allocation qty must be greater than zero. [${shadeErrors.join(", ")}]`, { type: "danger" }));
      return;
    } else if (!isEmpty(shadeErrors)) {
      dispatch(showNotification(`Shade qty cannot be less than allocation qty. [${shadeErrors.join(", ")}]`, { type: "danger" }));
      return;
    }

    let confirmed = true;
    if (!isEmpty(allocWarnings)) {
      // eslint-disable-next-line no-restricted-globals
      confirmed = confirm(`${allocWarnings?.join("\n\r")}\n\rAre you sure you want proceed?`);
    }
    if (!confirmed) {
      return;
    }

    const options = {
      url: `cut-orders`,
      method: "post",
      params: { orderId: id, groupOrderId: selectedRows[0]?.groupOrder?.id, lotType: "Bulk" },
      data: { flowInfoList, allowedShadeList }
    };
    mutate(options, {
      onSuccess: data => {
        dispatch(showNotification("Cut Order created successfully."));
        setVersion(v => v + 1);
        setTabIdx(0);
      }
    });
  };

  const onInlineActionClick = (event, action, row, onActionComplete) => {
    if (action === "delete") {
      if (isEmpty(row.cutOrder)) return;
      const options = { url: `cut-orders`, method: "delete", params: { orderId: id, cutOrderId: row.cutOrder?.id } };
      mutate(options, {
        onSuccess: data => {
          dispatch(showNotification("Cut Order deleted successfully."));
          setVersion(v => v + 1);
        }
      });
    }
  };

  return {
    busy,
    version,
    errorMessage,
    selectedKeys,
    order,
    flowInfoColumns,
    flowInfoRows,
    fabricInfoColumns,
    fabricInfoRows,
    shadeColumns,
    shadeRows,
    tabIdx,
    setTabIdx,
    setSelectedKeys,
    onInlineActionClick,
    createCutOrder
  };
};

export default useCutOrder;
